<script setup>
import {ref, computed, watch, onMounted} from 'vue'

const emit = defineEmits(['gradientChanged'])

const props = defineProps({
    gradient: {
        type: Object,
        required: true
    },
})

const colorStops = ref([])
const angle = ref(180)
const colorStopBeingEdited = ref(null)

const gradientStyle = computed(() => {
    const stops = colorStops.value
        .map(stop => `${stop.color} ${stop.position}%`)
        .join(', ')
    return `linear-gradient(to bottom, ${stops})`
})

const gradientWithAngleStyle = computed(() => {
    const stops = colorStops.value
        .map(stop => `${stop.color} ${stop.position}%`)
        .join(', ')
    return `linear-gradient(${angle.value}deg, ${stops})`
})

const addColorStop = () => {
    if (colorStops.value.length === 0) {
        colorStops.value = [
            {color: 'rgba(0, 0, 0, 0.25)', position: 0},
            {color: 'rgba(0, 0, 0, 0.5)', position: 100}
        ]
        return
    }
    const lastStop = colorStops.value[colorStops.value.length - 1]
    const newPosition = Math.min(lastStop.position + 20, 100)
    colorStops.value.push({
        color: 'rgba(0, 0, 0, 0.5)',
        position: newPosition
    })
}

const resetColorStops = () => {
    colorStops.value = [
        {color: 'rgba(0, 0, 0, 0.25)', position: 0},
        {color: 'rgba(0, 0, 0, 0.5)', position: 100}
    ]
}

const removeColorStop = (index) => {
    if (colorStops.value.length > 2) {
        colorStops.value.splice(index, 1)
    }
}

const updateColor = (index, color) => {
    colorStops.value[index].color = color
}

const updatePosition = (index, position) => {
    colorStops.value[index].position = position
}

const removeAllColorStops = () => {
    colorStops.value = []
}

const updateGradient = () => {
    const compositeGradient = {
        direction: 'to bottom',
        angle: angle.value,
        valid: true,
        colors: colorStops.value.map(stop => stop.color),
        positions: colorStops.value.map(stop => stop.position),
        positionsAsDecimals: colorStops.value.map(stop => parseFloat((stop.position / 100).toFixed(2))),
        stops: colorStops.value,
    }
    compositeGradient.colors = compositeGradient.colors.map(color => color || 'transparent')

    compositeGradient.stops = compositeGradient.stops.map(stop => {
        return {
            color: stop.color || 'transparent',
            position: stop.position
        }
    })

    compositeGradient.valid = compositeGradient.colors.some(color => color !== 'transparent')

    emit('gradientChanged', compositeGradient)
}

const displayAngleSetting = computed(() => {
    if (window.myflow.pagebuilderMode === 'app_page') {
        return false
    }
    return true
})

watch(() =>angle.value, (newAngle) => {
    updateGradient()
}, {deep: true})

watch(() => colorStops.value, (newStops) => {
    updateGradient()
}, {deep: true})

onMounted(() => {
    if (props.gradient) {
        colorStops.value = props.gradient.stops
        angle.value = props.gradient.angle ?? 180
    } else {
        colorStops.value = [
        ]
        angle.value = 180
    }
})

</script>

<template>
    <div>
        <div v-if="colorStops.length === 0" class="flex justify-center">
            <button-secondary @click="addColorStop">
                Add gradient
            </button-secondary>
        </div>
        <div v-if="colorStops.length > 0" class="pb-4">
            <ui-tabbed
                :track-state="false"
                :responsive="false"
            >
                <ui-tabbed-tab
                    :title="'Colors'"
                    value="gradient-colors"
                    :active="true"
                >
                    <div class="flex items-stretch">
                        <div
                            class="py-2 pr-0 w-24">
                            <card-sub
                                v-if="colorStops.length > 0"
                                class="rounded-wl mb-6 w-full h-full"
                                :style="{ background: gradientStyle }"
                            ></card-sub>
                        </div>
                        <div class="w-full space-y-8">
                            <div
                                v-for="(stop, index) in colorStops"
                                :key="index"
                            >
                                <card-sub
                                    v-if="colorStopBeingEdited === index"
                                    class="bg-white rounded-wl ml-2 my-2"
                                >
                                    <color-picker
                                        :color="stop.color"
                                        :debounce="100"
                                        :disable-alpha="false"
                                        :show-palette="true"
                                        format="rgb"
                                        @colorChanged="(color) => updateColor(index, color)"
                                        @clear-color="updateColor(index, null)"
                                    />
                                    <div class="mt-4">
                                        <input-label label="Position"/>
                                        <div class="flex">
                                            <input
                                                type="range"
                                                :value="stop.position"
                                                @input="(e) => updatePosition(index, parseInt(e.target.value))"
                                                min="0"
                                                max="100"
                                                class="w-full"
                                            />
                                            <span class="position-value">{{ stop.position }}%</span>
                                        </div>
                                    </div>
                                    <div class="flex justify-between mt-2">
                                        <div>
                                            <button-danger
                                                v-if="colorStops.length > 2"
                                                size="sm"
                                                @click="removeColorStop(index)"
                                            >
                                                <i class="fa-regular fa-trash"></i>
                                            </button-danger>
                                        </div>
                                        <button-secondary size="sm" @click="colorStopBeingEdited = null">
                                            {{ _mft('shared:done') }}
                                        </button-secondary>
                                    </div>
                                </card-sub>
                                <div
                                    class="pl-2"
                                    v-else
                                >
                                    <div
                                        :style="{backgroundColor: stop.color}"
                                        class="w-full h-12 rounded-wl border flex justify-end items-center px-4"
                                    >
                                        <button-default
                                            size="sm"
                                            @click="colorStopBeingEdited = index"
                                        >
                                            {{ _mft('shared:edit') }}
                                        </button-default>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="colorStops.length > 0"
                        class="mt-4 pb-2 flex justify-end space-x-2"
                    >
                        <button-danger
                            size="sm"
                            @click="removeAllColorStops"
                        >
                            {{ _mft('pagebuilder:styleEditor.setting.backgroundGradient.reset') }}
                        </button-danger>
                        <button-default size="sm" @click="addColorStop">
                            {{ _mft('pagebuilder:styleEditor.setting.backgroundGradient.addColorStop') }}
                        </button-default>
                    </div>
                </ui-tabbed-tab>
                <ui-tabbed-tab
                    v-if="displayAngleSetting"
                    :title="'Angle'"
                    value="gradient-angle"
                >
                    <div
                        v-if="colorStops.length > 0"
                        class="bg-white rounded-wl mx-2 mt-4"
                    >
                        <div
                            v-if="colorStops.length > 0"
                            class="rounded-wl mb-6 border border-gray-300 w-full h-64"
                            :style="{ background: gradientWithAngleStyle }"
                        ></div>
                        <div class="px-2 pb-2">
                            <input-label :label="_mft('pagebuilder:styleEditor.setting.backgroundGradient.angle')" />
                            <input-range
                                :min="0"
                                :max="360"
                                v-model:content="angle"
                            />
                        </div>
                    </div>
                </ui-tabbed-tab>
            </ui-tabbed>
        </div>
    </div>
</template>

<style scoped>
.position-value {
    min-width: 48px;
    text-align: right;
}
</style>
